/**
 * Shared form types, constants, validation, hydration, and payload building
 * for workflow rule create and edit flows.
 */
import type { WorkflowRuleDisplay } from "@/services/workflow/workflow.types";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ActionTypeValue =
	| "set_status"
	| "set_assignee"
	| "send_email"
	| "create_entity";

export interface ConditionRow {
	id: string;
	/** Field id: UUID for custom fields, plain string for built-in properties */
	fieldName: string;
	/** Human-readable label for the field (used as entityPropertyName in payload) */
	fieldLabel: string;
	/** Data type of the selected field (e.g. "string", "dateTime", "number") */
	dataType: string;
	operator: string;
	value: string;
	/**
	 * Predefined valid values for the field (e.g. dropdown, radio, checkbox options).
	 * When non-empty the condition value input renders a Select instead of a text field.
	 */
	validValues?: string[];
}

export type ActionParams =
	| { type: "set_status"; statusId: string }
	| { type: "set_assignee"; assignee: string; assigneeName?: string }
	| {
			type: "send_email";
			recipientType: "Assignee" | "Owner" | "Custom" | "";
			recipientEmail: string;
	  }
	| {
			type: "create_entity";
			entityClass: "asset" | "record" | "job" | "ticket" | "";
			entityTypeId: string;
			entityName: string;
			priorityId: string;
	  }
	| { type: "" };

export interface ActionRow {
	id: string;
	type: ActionTypeValue | "";
	params: ActionParams;
}

export type TriggerType =
	| ""
	| "entity_created"
	| "status_entered"
	| "status_exited";

export interface RuleFormDraft {
	name: string;
	description: string;
	triggerType: TriggerType;
	triggerStatusId: string;
	conditions: ConditionRow[];
	actions: ActionRow[];
	/** Sub-organization IDs scoping this rule. Empty array = no specific scoping set yet. */
	subOrganizationIds: string[];
}

export interface RuleFormValidationResult {
	ok: boolean;
	errors: Record<string, string>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const RULE_FORM_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const RULE_FORM_CONDITION_FIELDS = [
	{ id: "name", label: "Name", dataType: "text" },
	{ id: "description", label: "Description", dataType: "text" },
	{ id: "status", label: "Status", dataType: "text" },
	{ id: "assignee", label: "Assignee", dataType: "text" },
	{ id: "createdAt", label: "Created At", dataType: "datetime" },
	{ id: "updatedAt", label: "Updated At", dataType: "datetime" },
];

export const RULE_FORM_NO_VALUE_OPERATORS = new Set<string>([
	"isnull",
	"isnotnull",
]);

const NUMERIC_CONDITION_DATA_TYPES = new Set(["number", "integer", "decimal"]);

export function isNumericConditionDataType(dataType: string): boolean {
	return NUMERIC_CONDITION_DATA_TYPES.has(dataType.toLowerCase());
}

/**
 * Operators for a condition field, in the order returned by
 * GET /workflows/condition-operators-by-data-type.
 * Null when that dictionary is missing or has no entry for the data type.
 */
export function operatorsForDataType(
	dataType: string,
	operatorsByDataType: Record<string, string[]> | undefined,
): string[] | null {
	if (!operatorsByDataType || !dataType.trim()) return null;
	const key = dataType.toLowerCase();
	for (const [entryKey, operators] of Object.entries(operatorsByDataType)) {
		if (entryKey.toLowerCase() === key) return operators;
	}
	return null;
}

// ─── Factories ────────────────────────────────────────────────────────────────

export function makeEmptyActionRow(): ActionRow {
	return { id: crypto.randomUUID(), type: "", params: { type: "" } };
}

export function makeEmptyConditionRow(): ConditionRow {
	return {
		id: crypto.randomUUID(),
		fieldName: "",
		fieldLabel: "",
		dataType: "",
		operator: "",
		value: "",
	};
}

export function makeEmptyRuleFormDraft(): RuleFormDraft {
	return {
		name: "",
		description: "",
		triggerType: "",
		triggerStatusId: "",
		conditions: [],
		actions: [makeEmptyActionRow()],
		subOrganizationIds: [],
	};
}

export function makeParamsForActionType(
	type: ActionTypeValue | "",
): ActionParams {
	if (type === "set_status") return { type: "set_status", statusId: "" };
	if (type === "set_assignee") return { type: "set_assignee", assignee: "" };
	if (type === "send_email")
		return { type: "send_email", recipientType: "", recipientEmail: "" };
	if (type === "create_entity")
		return {
			type: "create_entity",
			entityClass: "",
			entityTypeId: "",
			entityName: "",
			priorityId: "",
		};
	return { type: "" };
}

// ─── Hydration ────────────────────────────────────────────────────────────────

/** Build a RuleFormDraft pre-populated from the saved rule's latestVersion (or activeVersion). */
export function hydrateRuleFormDraft(rule: WorkflowRuleDisplay): RuleFormDraft {
	const version = rule.latestVersion ?? rule.activeVersion;
	const triggerCfg = version?.trigger?.config as
		| Record<string, unknown>
		| null
		| undefined;
	const triggerType = (version?.trigger?.type ?? "") as TriggerType;
	const triggerStatusId =
		(triggerCfg?.statusId as string | undefined) ??
		(triggerCfg?.StatusId as string | undefined) ??
		"";

	const conditions: ConditionRow[] = (version?.condition?.conditions ?? []).map(
		(c) => {
			// Custom fields: columnDefinitionId = UUID, entityPropertyName = human label
			// Built-in fields: entityPropertyName = property name, columnDefinitionId = null
			const isCustom = !!c.columnDefinitionId;
			// Use || (not ??) so that an empty-string columnDefinitionId (returned by the
			// API for built-in fields) falls through to entityPropertyName.
			const rawFieldName = c.columnDefinitionId || c.entityPropertyName || "";

			// For built-in fields, match case-insensitively (the API may normalize
			// camelCase names to PascalCase, e.g. "createdAt" → "CreatedAt").
			// Normalize `fieldName` to the canonical id stored in the static list so
			// the Select's selectedKey always finds a matching item.
			const builtInField = isCustom
				? undefined
				: RULE_FORM_CONDITION_FIELDS.find(
						(f) => f.id.toLowerCase() === rawFieldName.toLowerCase(),
					);
			const fieldName = builtInField ? builtInField.id : rawFieldName;

			return {
				id: crypto.randomUUID(),
				fieldName,
				fieldLabel: c.entityPropertyName ?? "",
				dataType: builtInField?.dataType ?? "",
				operator: c.operator ?? "",
				value:
					c.compareToStringValue ?? c.compareToNumericValue?.toString() ?? "",
			};
		},
	);

	const actions: ActionRow[] = (version?.actions ?? [])
		.filter((a) => !!a.type)
		.map((a) => {
			const aType = a.type ?? "";
			// API may return PascalCase or camelCase — check both
			const cfg = a.config as Record<string, unknown> | null | undefined;
			let params: ActionParams = { type: "" };

			if (aType === "set_status") {
				const statusId =
					(cfg?.StatusId as string | undefined) ??
					(cfg?.statusId as string | undefined) ??
					"";
				params = { type: "set_status", statusId };
			} else if (aType === "set_assignee") {
				const assignee =
					(cfg?.UserId as string | undefined) ??
					(cfg?.userId as string | undefined) ??
					(cfg?.Assignee as string | undefined) ??
					(cfg?.assignee as string | undefined) ??
					"";
				const assigneeName =
					(cfg?.AssigneeName as string | undefined) ??
					(cfg?.assigneeName as string | undefined);
				params = { type: "set_assignee", assignee, assigneeName };
			} else if (aType === "send_email") {
				const recipientType =
					(cfg?.RecipientType as "Assignee" | "Owner" | "Custom" | undefined) ??
					(cfg?.recipientType as "Assignee" | "Owner" | "Custom" | undefined) ??
					"";
				const emailsArray = (cfg?.Emails ?? cfg?.emails) as
					| string[]
					| undefined;
				const recipientEmail =
					emailsArray?.[0] ??
					(cfg?.RecipientEmail as string | undefined) ??
					(cfg?.recipientEmail as string | undefined) ??
					"";
				params = { type: "send_email", recipientType, recipientEmail };
			} else if (aType === "create_entity") {
				const entityClass =
					(cfg?.EntityTypeClass as
						| "asset"
						| "record"
						| "job"
						| "ticket"
						| undefined) ??
					(cfg?.entityTypeClass as
						| "asset"
						| "record"
						| "job"
						| "ticket"
						| undefined) ??
					"";
				const entityTypeId =
					(cfg?.EntityTypeId as string | undefined) ??
					(cfg?.entityTypeId as string | undefined) ??
					"";
				const entityName =
					(cfg?.NameTemplate as string | undefined) ??
					(cfg?.nameTemplate as string | undefined) ??
					"";
				const priorityId =
					(cfg?.PriorityId as string | undefined) ??
					(cfg?.priorityId as string | undefined) ??
					"";
				params = {
					type: "create_entity",
					entityClass,
					entityTypeId,
					entityName,
					priorityId,
				};
			}

			return {
				id: crypto.randomUUID(),
				type: aType as ActionTypeValue | "",
				params,
			};
		});

	const subOrganizationIds = (rule.scopes ?? [])
		.map((s) => s.subOrganizationId)
		.filter((id): id is string => !!id);

	return {
		name: rule.name ?? "",
		description: rule.description ?? "",
		triggerType,
		triggerStatusId,
		conditions,
		actions: actions.length > 0 ? actions : [makeEmptyActionRow()],
		subOrganizationIds,
	};
}

// ─── Validation ────────────────────────────────────────────────────────────────

export function validateRuleFormDraft(
	draft: RuleFormDraft,
	entityKind: string,
): RuleFormValidationResult {
	const errors: Record<string, string> = {};

	if (!draft.name.trim()) errors.name = "Name is required";
	if (!draft.triggerType) errors.trigger = "Trigger is required";

	if (
		(draft.triggerType === "status_entered" ||
			draft.triggerType === "status_exited") &&
		!draft.triggerStatusId
	) {
		errors.triggerStatus = "Status is required";
	}

	draft.conditions.forEach((cond, i) => {
		const needsValue = !RULE_FORM_NO_VALUE_OPERATORS.has(cond.operator);
		if (
			!cond.fieldName ||
			!cond.operator ||
			(needsValue && !cond.value.trim())
		) {
			errors[`condition_${i}`] =
				"Complete or remove this condition to continue";
			return;
		}
		if (
			needsValue &&
			isNumericConditionDataType(cond.dataType) &&
			!Number.isFinite(Number(cond.value.trim()))
		) {
			errors[`condition_${i}`] = "Enter a valid number";
		}
	});

	if (draft.actions.length === 0) {
		errors.actions = "At least one action is required";
	}

	draft.actions.forEach((action, i) => {
		if (!action.type) {
			errors[`action_${i}_type`] = "Action type is required";
			return;
		}
		if (action.type === "set_status") {
			const p = action.params as Extract<ActionParams, { type: "set_status" }>;
			if (!p.statusId)
				errors[`action_${i}_statusId`] = "Target status is required";
		} else if (action.type === "set_assignee") {
			const p = action.params as Extract<
				ActionParams,
				{ type: "set_assignee" }
			>;
			if (!p.assignee?.trim())
				errors[`action_${i}_assignee`] = "Assignee is required";
		} else if (action.type === "send_email") {
			const p = action.params as Extract<ActionParams, { type: "send_email" }>;
			if (!p.recipientType)
				errors[`action_${i}_recipientType`] = "Recipient type is required";
			if (p.recipientType === "Custom") {
				if (!p.recipientEmail?.trim())
					errors[`action_${i}_recipientEmail`] = "Recipient email is required";
				else if (!RULE_FORM_EMAIL_REGEX.test(p.recipientEmail.trim()))
					errors[`action_${i}_recipientEmail`] = "Enter a valid email address";
			}
		} else if (action.type === "create_entity") {
			const p = action.params as Extract<
				ActionParams,
				{ type: "create_entity" }
			>;
			if (!p.entityClass)
				errors[`action_${i}_entityClass`] = "Entity class is required";
			if (p.entityClass && p.entityClass !== "asset" && !p.entityTypeId)
				errors[`action_${i}_entityTypeId`] = "Entity type is required";
			if (!p.entityName?.trim())
				errors[`action_${i}_entityName`] = "Entity name is required";
			if (p.entityClass === "ticket" && !p.priorityId?.trim())
				errors[`action_${i}_priorityId`] = "Priority is required";
		}
		if (entityKind === "RecordType" && action.type === "set_status") {
			errors[`action_${i}_type`] =
				"Set Status is not available for Record Types";
		}
	});

	return { ok: Object.keys(errors).length === 0, errors };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const UUID_REGEX =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Returns true when s looks like a UUID (custom field id). */
function isUuid(s: string): boolean {
	return UUID_REGEX.test(s);
}

export interface ConditionCompareFields {
	compareToStringValue: string | null;
	compareToNumericValue: number | null;
}

/**
 * Maps a condition row's data type and value to the API compare fields.
 * Numeric fields use compareToNumericValue; all others use compareToStringValue.
 */
export function buildConditionCompareFields(
	dataType: string,
	operator: string,
	value: string,
): ConditionCompareFields {
	if (RULE_FORM_NO_VALUE_OPERATORS.has(operator)) {
		return { compareToStringValue: null, compareToNumericValue: null };
	}

	const trimmed = value.trim();
	if (!trimmed) {
		return { compareToStringValue: null, compareToNumericValue: null };
	}

	if (isNumericConditionDataType(dataType)) {
		const numeric = Number(trimmed);
		return {
			compareToStringValue: null,
			compareToNumericValue: Number.isFinite(numeric) ? numeric : null,
		};
	}

	return {
		compareToStringValue: trimmed,
		compareToNumericValue: null,
	};
}

// ─── Payload builder ─────────────────────────────────────────────────────────

/** Build trigger/condition/actions payload for PUT …/versions/{versionId} */
export function buildRuleVersionPayload(draft: RuleFormDraft) {
	const triggerConfig: Record<string, unknown> =
		draft.triggerType === "status_entered" ||
		draft.triggerType === "status_exited"
			? { statusId: draft.triggerStatusId }
			: {};

	return {
		trigger: { type: draft.triggerType, config: triggerConfig },
		condition:
			draft.conditions.length > 0
				? {
						groupType: "all",
						conditions: draft.conditions.map((c) => ({
							...(isUuid(c.fieldName)
								? {
										columnDefinitionId: c.fieldName,
										entityPropertyName: c.fieldLabel || null,
									}
								: {
										columnDefinitionId: null,
										entityPropertyName: c.fieldName,
									}),
							operator: c.operator,
							...buildConditionCompareFields(c.dataType, c.operator, c.value),
						})),
					}
				: null,
		actions: draft.actions.map((action) => {
			if (action.type === "set_status") {
				const p = action.params as Extract<
					ActionParams,
					{ type: "set_status" }
				>;
				return { type: action.type, config: { statusId: p.statusId } };
			}
			if (action.type === "set_assignee") {
				const p = action.params as Extract<
					ActionParams,
					{ type: "set_assignee" }
				>;
				return {
					type: action.type,
					config: {
						userId: p.assignee.trim(),
						...(p.assigneeName ? { assigneeName: p.assigneeName } : {}),
					},
				};
			}
			if (action.type === "send_email") {
				const p = action.params as Extract<
					ActionParams,
					{ type: "send_email" }
				>;
				return {
					type: action.type,
					config: {
						recipientType: p.recipientType,
						...(p.recipientType === "Custom"
							? { emails: [p.recipientEmail.trim()] }
							: {}),
					},
				};
			}
			if (action.type === "create_entity") {
				const p = action.params as Extract<
					ActionParams,
					{ type: "create_entity" }
				>;
				return {
					type: action.type,
					config: {
						entityTypeClass: p.entityClass,
						entityTypeId: p.entityTypeId || null,
						nameTemplate: p.entityName.trim(),
						...(p.entityClass === "ticket" && p.priorityId?.trim()
							? { priorityId: p.priorityId.trim() }
							: {}),
					},
				};
			}
			return { type: action.type, config: {} };
		}),
	};
}

// ─── Entity kind map ──────────────────────────────────────────────────────────

export const ENTITY_TYPE_TO_KIND: Record<string, string> = {
	job: "JobType",
	ticket: "TicketType",
	record: "RecordType",
	asset: "AssetType",
};
